import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import castArray from 'lodash/castArray';
import get from 'lodash/get';

import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';

import { DialogTitleWithClose, SelectWithSearch, GroupedSelect, Alert } from 'ui';
import { useRegions } from 'shared/hooks';
import { useDeepCompareEffectNoCheck as useDeepCompareEffect } from 'use-deep-compare-effect';
import { fetchCountryRegions } from 'services';

import { theme } from 'theme';

const useStyles = makeStyles(() => ({
  city: {
    width: '70%',
    paddingRight: theme.spacing(2),
  },
  postcode: {
    width: '30%',
  },
  warningIcon: {
    marginRight: theme.spacing(1),
  },
  applyBtn: {
    width: 140,
  },
}));

const SearchFiltersModal = ({ open, onClose, onSubmit, searchQuery, extraMessage }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const regions = useRegions();

  const getInitialCountry = () => {
    const countryCode = get(searchQuery, 'country[0]', null);
    return countryCode && regions ? regions.data.find(({ iso }) => countryCode === iso) : null;
  };

  const getInitialValues = () => ({
    name: (searchQuery && searchQuery.query) || '',
    country: getInitialCountry(),
    region_code: (searchQuery && searchQuery.region_code) || '',
    city: (searchQuery && searchQuery.city) || '',
    postal_code: (searchQuery && searchQuery.postal_code) || '',
  });

  const [countryRegions, setCountryRegions] = useState({ isPending: false, data: [] });
  const [values, setValues] = useState(getInitialValues());

  useDeepCompareEffect(() => {
    setValues(getInitialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const parseCountryRegions = data => {
    return castArray(data)
      .filter(x => x)
      .map(({ regionName, regionCode }) => ({
        value: regionCode,
        label: regionName,
      }));
  };

  const getCountryRegions = async country => {
    setCountryRegions(data => ({ ...data, isPending: true }));
    try {
      const response = await fetchCountryRegions(country);
      setCountryRegions(() => ({
        isPending: false,
        data: parseCountryRegions(response.data),
      }));
    } catch (error) {
      setCountryRegions(() => ({ isPending: false, data: [] }));
    }
  };

  const handleChange = name => event => {
    const value = event && event.target ? event.target.value : event;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleCountryChange = country => {
    getCountryRegions(country);
    setValues({
      ...values,
      country,
    });
  };

  const handleSubmit = () => {
    const { country } = values;

    const cleanedValues = {
      ...values,
      country: country && country.iso ? country.iso : null,
    };

    onSubmit(cleanedValues);
  };

  useEffect(() => {
    handleCountryChange(values.country);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.country]);

  const countryRegionsAvailable = countryRegions.data && countryRegions.data.length;

  return (
    <Dialog fullWidth open={open}>
      <DialogTitleWithClose onClose={onClose}>{t('Refine your search')}</DialogTitleWithClose>
      <DialogContent>
        {extraMessage && <Alert message={t(extraMessage)} />}
        <TextField
          type="text"
          label={t('ENTITY.NAME')}
          fullWidth
          value={values.name}
          onChange={handleChange('name')}
        />
        <GroupedSelect
          options={regions.data}
          value={values.country}
          label={t('Country')}
          onChange={handleCountryChange}
          getOptionLabel={option => option.name}
          groupBy={option => option.continent}
        />
        <SelectWithSearch
          disabled={countryRegions.isPending || !countryRegionsAvailable}
          label={t('Region')}
          options={countryRegions.data}
          value={values.region_code}
          onChange={handleChange('region_code')}
        />
        <TextField
          className={classes.city}
          inputProps={{
            maxLength: 25,
          }}
          type="text"
          label="City"
          value={values.city}
          onChange={handleChange('city')}
        />
        <TextField
          className={classes.postcode}
          inputProps={{
            maxLength: 25,
          }}
          type="text"
          label="Postcode"
          value={values.postal_code}
          onChange={handleChange('postal_code')}
        />
        {values.country && !countryRegions.isPending && !countryRegionsAvailable && (
          <Box display="flex" mt={1}>
            <WarningIcon htmlColor={theme.palette.custom.warning} className={classes.warningIcon} />
            <Typography variant="body2">
              {t(
                'Currently there is no filter for regions available for your selection. Please provide a city name and / or postcode to find more accurate matches (national fomat for postcodes apply).',
              )}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.applyBtn}
          disabled={countryRegions.isPending}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          {t(countryRegions.isPending ? 'Loading...' : 'Search')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SearchFiltersModal.defaultProps = {
  searchQuery: null,
  extraMessage: null,
};

SearchFiltersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  extraMessage: PropTypes.string,
  searchQuery: PropTypes.shape({
    query: PropTypes.string,
    country: PropTypes.arrayOf(PropTypes.string),
    industry: PropTypes.arrayOf(PropTypes.string),
    ratingType: PropTypes.string,
    city: PropTypes.string,
    region_code: PropTypes.string,
    postal_code: PropTypes.string,
  }),
};

export default SearchFiltersModal;
